import { useMutation } from '@tanstack/react-query';
import { rpcClient, useInvalidateRpcQueryFn } from '@/api/rpc/client';
import type { CancelSessionInput, DeleteAuthStateInput } from '@erp/rpc';
import { useToast } from '@/context/ToastContext';

export function useCancelSession(options?: { onSuccess?: () => void }) {
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: CancelSessionInput) =>
      rpcClient.agent.cancelSession(input),
    onSuccess: (data) => {
      if (data.success) {
        showToast({
          title: 'Session Cancelled',
          message: data.message,
          color: 'info',
          icon: 'info',
        });
        options?.onSuccess?.();
      } else {
        showToast({
          title: 'Warning',
          message: data.message,
          color: 'warning',
          icon: 'warning',
        });
      }
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to cancel session',
        color: 'error',
        icon: 'x',
      });
    },
  });
}

export function useDeleteAuthState(options?: { onSuccess?: () => void }) {
  const invalidateRpcQueryFn = useInvalidateRpcQueryFn((orpc) => [
    orpc.agent.listAuthStates.key(),
  ]);
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: DeleteAuthStateInput) =>
      rpcClient.agent.deleteAuthState(input),
    onSuccess: (data) => {
      if (data.success) {
        showToast({
          title: 'Success',
          message: 'Auth state deleted successfully',
          color: 'success',
          icon: 'check',
        });
        invalidateRpcQueryFn({});
        options?.onSuccess?.();
      } else {
        showToast({
          title: 'Error',
          message: data.message || 'Failed to delete auth state',
          color: 'error',
          icon: 'x',
        });
      }
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to delete auth state',
        color: 'error',
        icon: 'x',
      });
    },
  });
}
